import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  FileText, 
  User, 
  CheckCircle2,
  Clock, 
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  Search,
  Loader2,
  Filter,
  AlertCircle,
  Check,
  X,
  RotateCcw
} from 'lucide-react';
import { toast } from "sonner";
import { getCurrentUser } from '@/data/users';
import { NotePracticeAnswers } from '@/components/NotePracticeAnswers';
import { ActivitySubmissionsSummary } from '@/components/ActivitySubmissionsSummary';

const API_URL = '';
const authHeader = () => { const t = localStorage.getItem('authToken'); return t ? { Authorization: `Bearer ${t}` } : {}; };

// Retry fetch for unreliable connections
const retryFetch = async (url, options, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || response.status < 500) return response;
      lastError = new Error(`Server error: ${response.status}`);
      if (i < maxRetries - 1) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    } catch (err) {
      lastError = err;
      if (i < maxRetries - 1) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    }
  }
  throw lastError || new Error('Request failed after retries');
};

export default function StudentSubmissionsPage() {
  const currentUser = getCurrentUser();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [gradingMarks, setGradingMarks] = useState({});
  const [gradingFeedback, setGradingFeedback] = useState({});
  const [questionMarks, setQuestionMarks] = useState({});
  const [submittingGrade, setSubmittingGrade] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [years, setYears] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      // Warm up DB first, then load
      fetch(`${API_URL}/api/warmup`).catch(() => {});
      setTimeout(() => loadSubmissions(), 500);
    }
  }, [currentUser?.id, filterYear]);

  const loadSubmissions = async (isRetry = false) => {
    setLoading(true);
    setLoadFailed(false);
    try {
      let url = `${API_URL}/api/ai-lessons/all-submissions?teacher_id=${currentUser.id}`;
      if (filterYear !== 'all') url += `&year_level=${filterYear}`;
      
      const response = await retryFetch(url, { headers: authHeader() });
      if (response.ok) {
        const data = await response.json();
        const subs = data.submissions || [];
        setSubmissions(subs);
        if (filterYear === 'all') setYears([...new Set(subs.map(x => x.year_level).filter(Boolean))].sort((a, b) => a - b));
        
        // Pre-fill auto-graded marks
        const marksMap = {};
        subs.forEach(s => {
          if (s.marks !== null) marksMap[s.id] = s.marks;
          else if (s.auto_marks !== null) marksMap[s.id] = s.auto_marks;
        });
        setGradingMarks(marksMap);

        // Auto-retry once if DB returned empty (cold start may give empty result)
        if (subs.length === 0 && !isRetry) {
          setTimeout(() => loadSubmissions(true), 4000);
        }
      } else {
        setLoadFailed(true);
        if (!isRetry) setTimeout(() => loadSubmissions(true), 4000);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      setLoadFailed(true);
      if (!isRetry) {
        setTimeout(() => loadSubmissions(true), 4000);
      } else {
        toast.error('Failed to load submissions — database may be waking up. Click Retry.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate total marks for a submission (auto + manual question marks)
  const calcTotalMarks = (sub) => {
    const autoMarks = sub.auto_marks || 0;
    const manualQuestions = (sub.auto_details || []).filter(d => !d.auto_graded);
    let manualTotal = 0;
    manualQuestions.forEach(d => {
      const key = `${sub.id}-q${d.question}`;
      const val = questionMarks[key];
      if (val !== undefined && val !== '') manualTotal += parseInt(val) || 0;
    });
    return autoMarks + manualTotal;
  };

  const handleGradeSubmission = async (submissionId) => {
    const sub = submissions.find(s => s.id === submissionId);
    const feedback = gradingFeedback[submissionId];
    
    if (!feedback) {
      toast.error('Please enter feedback');
      return;
    }

    // Check if any manual marks are missing - warn but don't block
    const manualQuestions = (sub?.auto_details || []).filter(d => !d.auto_graded);
    const missingCount = manualQuestions.filter(d => {
      const key = `${submissionId}-q${d.question}`;
      return questionMarks[key] === undefined || questionMarks[key] === '';
    }).length;
    
    if (missingCount > 0) {
      const proceed = window.confirm(
        `${missingCount} manual review question(s) have no marks entered — they will be graded as 0. Continue?`
      );
      if (!proceed) return;
    }

    const totalMarks = calcTotalMarks(sub);
    
    setSubmittingGrade(prev => ({ ...prev, [submissionId]: true }));
    try {
      const response = await retryFetch(
        `${API_URL}/api/ai-lessons/grade-submission/${submissionId}?teacher_id=${currentUser.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ marks: parseInt(totalMarks) || 0, feedback })
        }
      );
      if (response.ok) {
        toast.success('Submission graded successfully!');
        await loadSubmissions();
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || `Failed to grade (${response.status})`);
        console.error('Grade error:', response.status, errData);
      }
    } catch (error) {
      console.error('Error grading:', error);
      toast.error(`Error grading: ${error.message || 'Network error - please try again'}`);
    } finally {
      setSubmittingGrade(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const [resettingSubmission, setResettingSubmission] = useState({});
  const [resetDialog, setResetDialog] = useState({ open: false, submissionId: null, studentName: '' });

  const handleResetSubmission = (submissionId, studentName) => {
    setResetDialog({ open: true, submissionId, studentName });
  };

  const confirmResetSubmission = async () => {
    const { submissionId, studentName } = resetDialog;
    setResetDialog({ open: false, submissionId: null, studentName: '' });

    setResettingSubmission(prev => ({ ...prev, [submissionId]: true }));
    try {
      const response = await retryFetch(
        `${API_URL}/api/ai-lessons/reset-submission/${submissionId}?teacher_id=${currentUser.id}`,
        { method: 'DELETE', headers: authHeader() }
      );
      if (response.ok) {
        toast.success(`Submission reset for ${studentName}. They can now retake the test.`);
        await loadSubmissions();
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to reset submission');
      }
    } catch (error) {
      console.error('Error resetting submission:', error);
      toast.error('Failed to reset submission. Please try again.');
    } finally {
      setResettingSubmission(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      s.student_name?.toLowerCase().includes(search) ||
      s.assessment_title?.toLowerCase().includes(search)
    );
  });

  // Group by assessment
  const groupedByAssessment = filteredSubmissions.reduce((acc, s) => {
    const key = s.shared_id;
    if (!acc[key]) {
      acc[key] = {
        title: s.assessment_title,
        subject: s.subject,
        year_level: s.year_level,
        type: s.assessment_type,
        questions: s.questions,
        total_marks: s.total_marks,
        submissions: []
      };
    }
    acc[key].submissions.push(s);
    return acc;
  }, {});

  const pendingCount = submissions.filter(s => s.status === 'submitted').length;
  const gradedCount = submissions.filter(s => s.status === 'graded').length;
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sortBy, setSortBy] = useState('name'); // 'name', 'score', 'date'

  const toggleGroup = (id) => setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));

  const sortSubmissions = (subs) => {
    return [...subs].sort((a, b) => {
      if (sortBy === 'score') return (b.marks ?? b.auto_marks ?? -1) - (a.marks ?? a.auto_marks ?? -1);
      if (sortBy === 'date') return new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0);
      return (a.student_name || '').localeCompare(b.student_name || '');
    });
  };

  return (
    <>
    <div className="space-y-6" data-testid="student-submissions-page">
      <ActivitySubmissionsSummary />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="submissions-title">Student Submissions</h1>
          <p className="text-gray-500">Review and grade student assessment submissions</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-lg px-4 py-2" data-testid="total-submissions-count">
            {submissions.length} Total
          </Badge>
          {pendingCount > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 text-lg px-4 py-2" data-testid="pending-count">
              {pendingCount} Pending
            </Badge>
          )}
          {gradedCount > 0 && (
            <Badge className="bg-green-100 text-green-800 text-lg px-4 py-2" data-testid="graded-count">
              {gradedCount} Graded
            </Badge>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex gap-2">
              {['all', 'in_progress', 'submitted', 'graded'].map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                  className={filterStatus === status ? (status === 'submitted' ? 'bg-yellow-600' : status === 'graded' ? 'bg-green-600' : status === 'in_progress' ? 'bg-orange-600' : 'bg-blue-600') : ''}
                  data-testid={`filter-status-${status}`}
                >
                  {status === 'all' ? 'All Status' : status === 'submitted' ? 'Pending Review' : status === 'in_progress' ? 'In Progress' : 'Graded'}
                </Button>
              ))}
            </div>

            <div className="flex gap-2">
              {['all', ...years.map(String)].map(year => (
                <Button
                  key={year}
                  variant={filterYear === year ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterYear(year)}
                  className={filterYear === year ? 'bg-teal-600' : ''}
                  data-testid={`filter-year-${year}`}
                >
                  {year === 'all' ? 'All Years' : `Year ${year}`}
                </Button>
              ))}
            </div>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by student name or assessment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-submissions"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : Object.keys(groupedByAssessment).length > 0 ? (
        <div className="space-y-4">
          {/* Sort control */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Sort by:</span>
            {[{v:'name',l:'Name'},{v:'score',l:'Score'},{v:'date',l:'Date'}].map(s => (
              <button key={s.v} onClick={() => setSortBy(s.v)}
                className={`px-2 py-0.5 rounded text-xs font-medium ${sortBy === s.v ? 'bg-emerald-100 text-emerald-700' : 'hover:bg-gray-100 text-gray-600'}`}
              >{s.l}</button>
            ))}
          </div>

          {Object.entries(groupedByAssessment).map(([sharedId, group]) => {
            const isCollapsed = collapsedGroups[sharedId];
            const groupPending = group.submissions.filter(s => s.status === 'submitted').length;
            const groupGraded = group.submissions.filter(s => s.status === 'graded').length;
            const avgScore = group.submissions.filter(s => s.marks != null).length > 0
              ? Math.round(group.submissions.filter(s => s.marks != null).reduce((sum, s) => sum + s.marks, 0) / group.submissions.filter(s => s.marks != null).length)
              : null;

            return (
            <Card key={sharedId} className="border-0 shadow-sm overflow-hidden" data-testid={`assessment-group-${sharedId}`}>
              {/* Assessment Header - Clickable to collapse/expand */}
              <div
                className="bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-3 border-b cursor-pointer hover:from-emerald-100 hover:to-teal-100 transition-colors"
                onClick={() => toggleGroup(sharedId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-emerald-900 text-sm truncate">{group.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{group.subject?.toUpperCase()}</Badge>
                      <Badge variant="outline" className="text-xs">Year {group.year_level}</Badge>
                      {group.type && <Badge variant="outline" className="text-xs capitalize">{group.type}</Badge>}
                      {group.total_marks && <Badge variant="outline" className="text-xs">Total: {group.total_marks} marks</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <Badge className="bg-white border text-gray-700 text-xs">
                      {group.submissions.length} student{group.submissions.length !== 1 ? 's' : ''}
                    </Badge>
                    {groupPending > 0 && <Badge className="bg-yellow-100 text-yellow-700 text-xs">{groupPending} pending</Badge>}
                    {groupGraded > 0 && <Badge className="bg-green-100 text-green-700 text-xs">{groupGraded} graded</Badge>}
                    {avgScore !== null && <Badge className="bg-blue-100 text-blue-700 text-xs">Avg: {avgScore}/{group.total_marks || '?'}</Badge>}
                    {isCollapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>
              </div>

              {/* Compact Table View */}
              {!isCollapsed && (
                <div>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="col-span-1">#</div>
                    <div className="col-span-3">Student</div>
                    <div className="col-span-2">Submitted</div>
                    <div className="col-span-2 text-center">Auto Score</div>
                    <div className="col-span-2 text-center">Final Score</div>
                    <div className="col-span-2 text-center">Status</div>
                  </div>

                  {/* Table Rows */}
                  {sortSubmissions(group.submissions).map((sub, idx) => (
                    <div key={sub.id} data-testid={`submission-card-${sub.id}`}>
                      <div
                        className={`grid grid-cols-12 gap-2 px-5 py-2.5 items-center cursor-pointer transition-colors text-sm ${
                          expandedSubmission === sub.id ? 'bg-emerald-50 border-l-2 border-emerald-500' : 'hover:bg-gray-50 border-l-2 border-transparent'
                        } ${idx !== group.submissions.length - 1 ? 'border-b border-gray-100' : ''}`}
                        onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
                        data-testid={`submission-toggle-${sub.id}`}
                      >
                        <div className="col-span-1 text-gray-400 text-xs">{idx + 1}</div>
                        <div className="col-span-3 font-medium text-gray-800 truncate">{sub.student_name}</div>
                        <div className="col-span-2 text-gray-500 text-xs">
                          {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </div>
                        <div className="col-span-2 text-center">
                          {sub.auto_marks != null ? (
                            <span className="text-blue-600 font-medium text-xs">{sub.auto_marks}/{sub.auto_total || '?'}</span>
                          ) : <span className="text-gray-300">—</span>}
                        </div>
                        <div className="col-span-2 text-center">
                          {sub.status === 'graded' ? (
                            <span className="text-emerald-700 font-bold">{sub.marks}/{sub.total_marks || '?'}</span>
                          ) : <span className="text-gray-300">—</span>}
                        </div>
                        <div className="col-span-2 text-center flex items-center justify-center gap-1.5">
                          {sub.status === 'graded' ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-yellow-500" />
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetSubmission(sub.id, sub.student_name);
                            }}
                            disabled={resettingSubmission[sub.id]}
                            className="ml-1 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                            title="Reset test — allow student to retake"
                            data-testid={`reset-submission-${sub.id}`}
                          >
                            {resettingSubmission[sub.id] ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedSubmission === sub.id && (
                        <div className="px-5 pb-4 bg-gray-50 border-b">
                          {/* Student Answers with Auto-Grade */}
                          <div className="mt-3 space-y-2">
                            <h5 className="font-medium text-gray-700 text-sm mb-2">Student Answers:</h5>
                            {(sub.answers || []).map((ans, qi) => {
                              const question = sub.questions?.[qi];
                              const autoDetail = sub.auto_details?.find(d => d.question === qi + 1);
                              return (
                                <div 
                                  key={qi} 
                                  className={`text-sm border-l-2 pl-3 ${
                                    autoDetail?.auto_graded 
                                      ? (autoDetail.is_correct ? 'border-green-400 bg-green-50/50' : 'border-red-300 bg-red-50/50') 
                                      : 'border-gray-200'
                                  } rounded-r p-2`}
                                  data-testid={`answer-${sub.id}-q${qi}`}
                                >
                                  <p className="font-medium text-gray-700">
                                    Q{qi + 1}: {question?.question || question?.text || `Question ${qi + 1}`}
                                  </p>
                                  <p className="text-blue-700 mt-1">
                                    Answer: {typeof ans === 'object' ? (ans.answer || JSON.stringify(ans)) : String(ans)}
                                  </p>
                                  {autoDetail?.auto_graded && (
                                    <div className="mt-1 flex items-center gap-2 text-xs">
                                      {autoDetail.is_correct ? (
                                        <span className="text-green-600 font-medium flex items-center gap-1">
                                          <Check className="h-3 w-3" /> Correct ({autoDetail.marks_awarded}/{autoDetail.marks_possible})
                                        </span>
                                      ) : (
                                        <span className="text-red-500 font-medium flex items-center gap-1">
                                          <X className="h-3 w-3" /> Incorrect (0/{autoDetail.marks_possible}) — Correct: {autoDetail.correct_answer}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {autoDetail && !autoDetail.auto_graded && (
                                    <div className="mt-1 flex items-center gap-3">
                                      <span className="text-xs text-amber-600 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" /> Manual review needed
                                      </span>
                                      {sub.status !== 'graded' && (
                                        <div className="flex items-center gap-1.5">
                                          <Label className="text-xs font-medium text-gray-600">Mark:</Label>
                                          <Input
                                            type="number"
                                            min="0"
                                            max={question?.marks || 5}
                                            placeholder={`0-${question?.marks || '?'}`}
                                            value={questionMarks[`${sub.id}-q${qi + 1}`] ?? ''}
                                            onChange={(e) => setQuestionMarks(prev => ({ ...prev, [`${sub.id}-q${qi + 1}`]: e.target.value }))}
                                            className="w-20 h-7 text-xs"
                                            data-testid={`manual-mark-${sub.id}-q${qi + 1}`}
                                          />
                                          <span className="text-xs text-gray-500">/ {question?.marks || '?'}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Grading Section */}
                          {sub.status === 'graded' ? (
                            <div className="mt-4 bg-green-50 rounded-lg p-3 border border-green-200" data-testid={`graded-result-${sub.id}`}>
                              <h5 className="font-medium text-green-800 flex items-center gap-2 mb-1 text-sm">
                                <CheckCircle2 className="h-4 w-4" /> Graded
                              </h5>
                              <p className="text-sm"><strong>Marks:</strong> {sub.marks}/{sub.total_marks || '?'}</p>
                              <p className="text-sm mt-1"><strong>Feedback:</strong> {sub.feedback}</p>
                              {sub.graded_at && (
                                <p className="text-xs text-gray-500 mt-1">Graded: {new Date(sub.graded_at).toLocaleString()}</p>
                              )}
                              <div className="mt-2 flex justify-end">
                                <Button
                                  onClick={() => handleResetSubmission(sub.id, sub.student_name)}
                                  disabled={resettingSubmission[sub.id]}
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`reset-graded-${sub.id}`}
                                >
                                  {resettingSubmission[sub.id] ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                  )}
                                  Reset Test
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4 bg-white rounded-lg p-3 border" data-testid={`grading-form-${sub.id}`}>
                              {sub.auto_marks !== null && sub.auto_marks !== undefined && (
                                <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                                  <strong className="text-blue-700">Auto-graded:</strong> {sub.auto_marks}/{sub.auto_total || '?'} marks
                                  {sub.auto_details && (() => {
                                    const manual = sub.auto_details.filter(d => !d.auto_graded).length;
                                    return manual > 0 
                                      ? <span className="text-amber-600 ml-2">({manual} question{manual > 1 ? 's' : ''} — enter marks above)</span> 
                                      : <span className="text-green-600 ml-2">(all auto-graded)</span>;
                                  })()}
                                </div>
                              )}
                              <div className="text-sm bg-gray-50 border rounded p-2 mb-2 flex items-center justify-between">
                                <span className="font-medium text-gray-700">Total Marks:</span>
                                <span className="text-lg font-bold text-emerald-700">{calcTotalMarks(sub)} / {sub.total_marks || '?'}</span>
                              </div>
                              <div>
                                <Label className="text-sm font-medium flex items-center gap-1 mb-1">
                                  <MessageSquare className="h-3.5 w-3.5" /> Teacher Feedback
                                </Label>
                                <Textarea
                                  placeholder="Write feedback for the student..."
                                  value={gradingFeedback[sub.id] || ''}
                                  onChange={(e) => setGradingFeedback(prev => ({ ...prev, [sub.id]: e.target.value }))}
                                  className="min-h-[60px]"
                                  data-testid={`grade-feedback-${sub.id}`}
                                />
                              </div>
                              <div className="mt-2 flex justify-end gap-2">
                                <Button
                                  onClick={() => handleResetSubmission(sub.id, sub.student_name)}
                                  disabled={resettingSubmission[sub.id]}
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`reset-btn-expanded-${sub.id}`}
                                >
                                  {resettingSubmission[sub.id] ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                  )}
                                  Reset Test
                                </Button>
                                <Button
                                  onClick={() => handleGradeSubmission(sub.id)}
                                  disabled={submittingGrade[sub.id]}
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`submit-grade-${sub.id}`}
                                >
                                  {submittingGrade[sub.id] ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4 mr-2" />
                                  )}
                                  Submit Grade
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2" data-testid="no-submissions-message">
                {loadFailed ? 'Failed to load submissions' : 'No submissions found'}
              </p>
              <p className="text-sm">
                {loadFailed
                  ? 'Database may still be waking up.'
                  : filterStatus !== 'all' || searchTerm 
                    ? 'Try adjusting your filters or search term.'
                    : 'Student submissions will appear here once they submit their work from shared assessments.'}
              </p>
              {(loadFailed || (submissions.length === 0 && filterStatus === 'all' && !searchTerm)) && (
                <button
                  onClick={() => loadSubmissions()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  data-testid="retry-submissions-btn"
                >
                  Retry
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      <NotePracticeAnswers teacherId={currentUser?.id} />
    </div>

    {/* Reset Confirmation Dialog */}
    <AlertDialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, submissionId: null, studentName: '' })}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Test for {resetDialog.studentName}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete their current attempt so they can retake the test. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmResetSubmission} className="bg-red-600 hover:bg-red-700">
            Reset Test
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
